import { useEffect, useState } from "react";
import { Menu, CalendarClock, CreditCard, Lock } from "lucide-react";
import { NAV_LINKS } from "@/data/site";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 border-b ${
        scrolled ? "bg-white/95 backdrop-blur-md border-[#E2E2DE] shadow-sm" : "bg-transparent border-transparent"
      }`}
    >
      <div className="flex items-center justify-between px-6 md:px-12 lg:px-24 h-20">
        <a href="#home" data-testid="nav-logo-link" className="flex items-center gap-3">
          <span className="bg-white px-2.5 py-1.5">
            <img src="/assets/logo.png" alt="Apex Home Renovation LLC" className="h-9 w-auto" />
          </span>
        </a>

        <nav className="hidden xl:flex items-center gap-7" data-testid="nav-desktop-menu">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-[11px] tracking-[0.18em] font-medium ${scrolled ? "text-[#1A2B45]" : "text-gray-200"} hover:text-[#D4AF37] transition-colors duration-300`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          <a
            href="#consultation"
            data-testid="nav-book-consultation-btn"
            className="bg-[#0A1F3C] text-white border border-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#050505] transition-colors duration-300 px-5 py-2.5 uppercase tracking-widest text-[11px] font-semibold"
          >
            Book a Consultation
          </a>
          <a
            href="#payments"
            data-testid="nav-make-payment-btn"
            className="bg-[#E5E4E2] text-[#050505] hover:bg-white transition-colors duration-300 px-5 py-2.5 uppercase tracking-widest text-[11px] font-semibold"
          >
            Make a Payment
          </a>
          <a
            href="/admin"
            data-testid="nav-admin-login-link"
            aria-label="Admin login"
            title="Admin Login"
            className={`${scrolled ? "text-[#1A2B45] border-[#D9D9D4]" : "text-gray-200 border-white/30"} border hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300 p-2.5`}
          >
            <Lock className="h-4 w-4" />
          </a>
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="nav-mobile-menu-btn"
              className={`xl:hidden ${scrolled ? "text-[#0A1F3C]" : "text-white"} p-2`}
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-white border-l border-[#E2E2DE] w-80">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <div className="flex flex-col gap-1 mt-8" data-testid="nav-mobile-menu">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-sm tracking-[0.18em] font-medium text-[#1A2B45] hover:text-[#D4AF37] py-3 border-b border-[#EFEFEA] transition-colors duration-300"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#consultation"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-book-consultation-btn"
                className="mt-6 flex items-center justify-center gap-2 bg-[#0A1F3C] text-white border border-[#D4AF37] px-5 py-3 uppercase tracking-widest text-xs font-semibold"
              >
                <CalendarClock className="h-4 w-4" /> Book a Consultation
              </a>
              <a
                href="#payments"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-make-payment-btn"
                className="flex items-center justify-center gap-2 bg-[#E5E4E2] text-[#050505] px-5 py-3 uppercase tracking-widest text-xs font-semibold"
              >
                <CreditCard className="h-4 w-4" /> Make a Payment
              </a>
              <a
                href="/admin"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-admin-login-link"
                className="mt-6 flex items-center justify-center gap-2 border border-[#D9D9D4] text-[#5A6A85] hover:border-[#D4AF37] hover:text-[#C9A227] transition-colors duration-300 px-5 py-3 uppercase tracking-widest text-xs font-semibold"
              >
                <Lock className="h-4 w-4" /> Admin Login
              </a>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
